// Tonk (Tunk) — pure rules engine + computer players.
//
// No I/O (imports only the shared types), so the same file runs in the
// `games` edge function (Deno), in the browser (spread checks, hand totals)
// and under node tests. The GameDef that plugs it into the table framework is
// at the bottom. The player-facing rules text lives in
// src/components/games/tonk/tonkRules.ts — keep the two in step.
//
// Rules (house defaults, after pagat.com/rummy/tonk.html):
//  - 2–4 players, 52 cards, 5 each. The next card turns up to start the
//    discard pile; the rest is the stock. Dealer rotates; left of the dealer
//    plays first.
//  - Values: A = 1, 2–10 face value, J/Q/K = 10.
//  - Tonk: a dealt hand worth 49 or 50 wins at once and pays double. Two or
//    more tonks at once = a push (nobody scores).
//  - Turn: either DROP (before drawing) or draw (stock or the top discard),
//    then lay down spreads / hit spreads as you like, then discard one card.
//    You can't throw back the discard you just took (unless it's your last
//    card).
//  - Spread: 3–4 of a kind, or 3+ in a row in one suit (ace low only).
//    Hit: add one card to any spread on the table.
//  - Empty hand (by spreading, hitting or the final discard) = you go out
//    and win.
//  - Drop: everyone shows. If nobody's total ties or beats the dropper's,
//    the dropper wins. Otherwise the dropper is caught: the lowest other
//    hand wins (ties: first in turn order after the dropper) and it pays
//    double.
//  - Stock runs out (after the turn that drew the last card): lowest total
//    wins; ties go to the tied player who would have played next.
//  - Scoring: a hand winner scores 1 point per opponent, 2 per opponent for
//    a tonk or a catch. After the chosen number of hands (lobby option, 3 / 5
//    / 7 / 10; default 5) the highest score wins; a tie for the lead plays
//    extra hands (up to 5), then it's a draw between the leaders.

import { RuleError, type GameDef, type SeatResult } from './types.ts';
export { RuleError };

export type Card = string; // rank + suit, e.g. 'AS', 'TD', '2C'
export const SUITS = ['C', 'D', 'H', 'S'] as const;
/** Run order: aces low. */
export const RANKS = 'A23456789TJQK';
export const SEAT_NAMES = ['North', 'East', 'South', 'West'];
export const MAX_SEATS = 4;
/** privs row holding the stock (no seat sits here, so nobody can read it). */
export const STOCK_ROW = 99;
export const HAND_SIZE = 5;
export const HAND_OPTIONS = [3, 5, 7, 10];
export const DEFAULT_HANDS = 5;
export const MAX_EXTRA_HANDS = 5;

export const suitOf = (c: Card) => c[1];
export const rankOf = (c: Card) => RANKS.indexOf(c[0]);
export const cardValue = (c: Card) => Math.min(10, rankOf(c) + 1);
export const handTotal = (hand: Card[]) => hand.reduce((s, c) => s + cardValue(c), 0);
const RANK_WORD: Record<string, string> = { A: 'A', T: '10', J: 'J', Q: 'Q', K: 'K' };
const SUIT_SYM: Record<string, string> = { C: '♣', D: '♦', H: '♥', S: '♠' };
export const cardText = (c: Card) => `${RANK_WORD[c[0]] ?? c[0]}${SUIT_SYM[c[1]] ?? ''}`;

export interface Spread { id: number; owner: number; kind: 'set' | 'run'; cards: Card[] }

export type HandHow = 'tonk' | 'out' | 'drop' | 'caught' | 'stock' | 'push';
export interface HandResult {
  how: HandHow;
  /** Hand winner (null for a push). */
  winner: number | null;
  /** Who dropped (drop / caught). */
  dropper: number | null;
  /** Tonk hands (tonk / push). */
  tonks: number[];
  /** Every active seat's final total (null for empty seats). */
  totals: (number | null)[];
  /** The hands shown down at the end (null for empty seats). */
  hands: (Card[] | null)[];
  /** Points scored this hand per seat. */
  points: number[];
}

export interface TonkState {
  phase: 'draw' | 'play' | 'hand_over' | 'game_over';
  handNo: number;
  /** Hands in a game (from the lobby option). */
  handsToPlay: number;
  dealer: number;
  turn: number;
  /** Seats playing this game (fixed at the start). */
  active: number[];
  /** Cards held per seat (public). */
  counts: number[];
  spreads: Spread[];
  nextSpreadId: number;
  /** Face-up discard pile, top = last. */
  discard: Card[];
  stockCount: number;
  /** The discard this player took this turn (can't be thrown straight back). */
  takenFromDiscard: Card | null;
  scores: number[];
  lastHand: HandResult | null;
  /** Game winner; null while playing or for a draw. */
  winner: number | null;
  /** Leaders sharing a drawn game. */
  drawn: number[];
}

export function fullDeck(): Card[] {
  const d: Card[] = [];
  for (const s of SUITS) for (const r of RANKS) d.push(r + s);
  return d;
}

export function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Display order: by suit, then rank (aces low). */
export function sortHand(hand: Card[]): Card[] {
  const order = { C: 0, D: 1, S: 2, H: 3 } as Record<string, number>;
  return hand.slice().sort((a, b) => order[suitOf(a)] - order[suitOf(b)] || rankOf(a) - rankOf(b));
}
const byRank = (cs: Card[]) => cs.slice().sort((a, b) => rankOf(a) - rankOf(b));

/** Next active seat clockwise after `seat`. */
export function nextActive(active: number[], seat: number): number {
  for (let k = 1; k <= MAX_SEATS; k++) {
    const s = (seat + k) % MAX_SEATS;
    if (active.includes(s)) return s;
  }
  return seat;
}
/** Active seats in turn order starting after `seat`. */
function orderAfter(active: number[], seat: number): number[] {
  const out: number[] = [];
  let s = seat;
  for (let k = 0; k < active.length; k++) { s = nextActive(active, s); out.push(s); }
  return out;
}

// ---------------------------------------------------------------------------
// Spreads
// ---------------------------------------------------------------------------

export function isSet(cards: Card[]): boolean {
  return cards.length >= 3 && cards.length <= 4 && cards.every((c) => c[0] === cards[0][0]);
}
export function isRun(cards: Card[]): boolean {
  if (cards.length < 3 || !cards.every((c) => suitOf(c) === suitOf(cards[0]))) return false;
  const r = byRank(cards).map(rankOf);
  return r.every((v, i) => i === 0 || v === r[i - 1] + 1);
}
export function spreadKind(cards: Card[]): 'set' | 'run' | null {
  return isSet(cards) ? 'set' : isRun(cards) ? 'run' : null;
}
export function canHit(spread: Pick<Spread, 'kind' | 'cards'>, card: Card): boolean {
  if (spread.cards.includes(card)) return false;
  if (spread.kind === 'set') return card[0] === spread.cards[0][0];
  if (suitOf(card) !== suitOf(spread.cards[0])) return false;
  const r = spread.cards.map(rankOf);
  const v = rankOf(card);
  return v === Math.min(...r) - 1 || v === Math.max(...r) + 1;
}

/** Every valid spread that can be laid from `hand` (sets of 3–4, runs of 3+). */
export function findSpreads(hand: Card[]): Card[][] {
  const out: Card[][] = [];
  const ranks = new Map<string, Card[]>();
  for (const c of hand) ranks.set(c[0], [...(ranks.get(c[0]) || []), c]);
  for (const cs of ranks.values()) {
    if (cs.length === 4) out.push(cs.slice());
    if (cs.length >= 3) for (let skip = 0; skip < cs.length; skip++) {
      if (cs.length === 3 && skip > 0) break;
      out.push(cs.length === 3 ? cs.slice() : cs.filter((_, i) => i !== skip));
    }
  }
  for (const s of SUITS) {
    const cs = byRank(hand.filter((c) => suitOf(c) === s));
    for (let i = 0; i < cs.length; i++) {
      for (let j = i + 2; j < cs.length; j++) {
        const seg = cs.slice(i, j + 1);
        if (isRun(seg)) out.push(seg); else break;
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Dealing and hand end
// ---------------------------------------------------------------------------

export function activeSeats(seats: (unknown | null)[]): number[] {
  return seats.map((s, i) => (s ? i : -1)).filter((i) => i >= 0 && i < MAX_SEATS);
}

export function clampHands(n: unknown): number {
  const v = Number(n);
  return HAND_OPTIONS.includes(v) ? v : DEFAULT_HANDS;
}

/**
 * Deal a hand. `prev` carries scores, seats and the dealer forward.
 * Returns the public state, each seat's hand and the stock.
 */
export function deal(prev: TonkState | null, rng: () => number, active: number[], handsToPlay = DEFAULT_HANDS):
  { state: TonkState; hands: Record<number, Card[]>; stock: Card[] } {
  const act = prev ? prev.active : active.slice().sort((a, b) => a - b);
  if (act.length < 2) throw new RuleError('Tonk needs at least 2 players');
  const dealer = prev ? nextActive(act, prev.dealer) : act[Math.floor(rng() * act.length)];
  const deck = shuffle(fullDeck(), rng);
  const hands: Record<number, Card[]> = {};
  let k = 0;
  for (const s of orderAfter(act, dealer)) { hands[s] = sortHand(deck.slice(k, k + HAND_SIZE)); k += HAND_SIZE; }
  const up = deck[k++];
  const stock = deck.slice(k);
  const counts = [0, 0, 0, 0];
  for (const s of act) counts[s] = HAND_SIZE;
  let state: TonkState = {
    phase: 'draw', handNo: (prev?.handNo ?? 0) + 1, handsToPlay: prev?.handsToPlay ?? handsToPlay,
    dealer, turn: nextActive(act, dealer), active: act, counts,
    spreads: [], nextSpreadId: 1, discard: [up], stockCount: stock.length, takenFromDiscard: null,
    scores: prev?.scores.slice() ?? [0, 0, 0, 0], lastHand: null, winner: null, drawn: [],
  };
  // Tonk: a dealt 49 or 50 wins at once (two or more = a push).
  const tonks = act.filter((s) => { const t = handTotal(hands[s]); return t === 49 || t === 50; });
  if (tonks.length === 1) state = endHand(state, hands, 'tonk', tonks[0], null, tonks);
  else if (tonks.length > 1) state = endHand(state, hands, 'push', null, null, tonks);
  return { state, hands, stock };
}

/** Close the hand: show the hands, pay the winner, maybe end the game. */
function endHand(s: TonkState, hands: Record<number, Card[]>, how: HandHow, winner: number | null, dropper: number | null, tonks: number[] = []): TonkState {
  const opp = s.active.length - 1;
  const points = [0, 0, 0, 0];
  if (winner !== null) points[winner] = opp * (how === 'tonk' || how === 'caught' ? 2 : 1);
  const scores = s.scores.map((v, i) => v + points[i]);
  const totals = [0, 1, 2, 3].map((i) => (s.active.includes(i) ? handTotal(hands[i] || []) : null));
  const shown = [0, 1, 2, 3].map((i) => (s.active.includes(i) ? sortHand(hands[i] || []) : null));
  const lastHand: HandResult = { how, winner, dropper, tonks, totals, hands: shown, points };
  let next: TonkState = { ...s, scores, lastHand, phase: 'hand_over', takenFromDiscard: null };
  if (s.handNo >= s.handsToPlay) {
    const top = Math.max(...s.active.map((i) => scores[i]));
    const leaders = s.active.filter((i) => scores[i] === top);
    if (leaders.length === 1) next = { ...next, phase: 'game_over', winner: leaders[0] };
    else if (s.handNo >= s.handsToPlay + MAX_EXTRA_HANDS) next = { ...next, phase: 'game_over', winner: null, drawn: leaders };
  }
  return next;
}

/** Lowest total wins; ties go to the first in `order`. */
function lowest(order: number[], hands: Record<number, Card[]>): number {
  let best = order[0];
  for (const s of order) if (handTotal(hands[s]) < handTotal(hands[best])) best = s;
  return best;
}

// ---------------------------------------------------------------------------
// Moves
// ---------------------------------------------------------------------------

export type TonkMove =
  | { type: 'drop' }
  | { type: 'draw'; from: 'stock' | 'discard' }
  | { type: 'spread'; cards: Card[] }
  | { type: 'hit'; card: Card; spread: number }
  | { type: 'discard'; card: Card };

export interface MoveOut { state: TonkState; hands: Record<number, Card[]>; stock: Card[] }

function handsOf(s: TonkState, privs: Record<number, any>): Record<number, Card[]> {
  const out: Record<number, Card[]> = {};
  for (const i of s.active) out[i] = Array.isArray(privs[i]) ? (privs[i] as Card[]).slice() : [];
  return out;
}

/** After a spread / hit / discard: an empty hand goes out. */
function afterShed(s: TonkState, hands: Record<number, Card[]>, seat: number): TonkState {
  return hands[seat].length === 0 ? endHand(s, hands, 'out', seat, null) : s;
}

const isCard = (c: unknown): c is Card => typeof c === 'string' && c.length === 2 && RANKS.includes(c[0]) && (SUITS as readonly string[]).includes(c[1]);

export function applyMove(s: TonkState, privs: Record<number, any>, seat: number, m: TonkMove): MoveOut {
  if (s.phase !== 'draw' && s.phase !== 'play') throw new RuleError('The hand is over');
  if (s.turn !== seat) throw new RuleError('Not your turn');
  const hands = handsOf(s, privs);
  let stock: Card[] = Array.isArray(privs[STOCK_ROW]) ? (privs[STOCK_ROW] as Card[]).slice() : [];
  const hand = hands[seat];
  if (!hand) throw new RuleError('You are not in this hand');
  const counts = s.counts.slice();
  const type = (m as any)?.type;
  if (!['drop', 'draw', 'spread', 'hit', 'discard'].includes(type)) throw new RuleError('Unknown move');

  if (type === 'drop') {
    if (s.phase !== 'draw') throw new RuleError('Drop at the start of your turn, before you draw');
    const mine = handTotal(hand);
    const others = orderAfter(s.active, seat).filter((x) => x !== seat);
    const catchers = others.filter((x) => handTotal(hands[x]) <= mine);
    const state = catchers.length
      ? endHand(s, hands, 'caught', lowest(catchers, hands), seat)
      : endHand(s, hands, 'drop', seat, seat);
    return { state, hands, stock };
  }

  if (type === 'draw') {
    if (s.phase !== 'draw') throw new RuleError('You already drew this turn');
    const from = (m as any).from;
    let card: Card;
    let taken: Card | null = null;
    let discard = s.discard;
    if (from === 'stock') {
      if (!stock.length) throw new RuleError('The stock is empty');
      card = stock[0]; stock = stock.slice(1);
    } else if (from === 'discard') {
      if (!s.discard.length) throw new RuleError('The discard pile is empty');
      card = s.discard[s.discard.length - 1]; discard = s.discard.slice(0, -1); taken = card;
    } else throw new RuleError('Draw from the stock or the discard pile');
    hands[seat] = sortHand([...hand, card]);
    counts[seat] += 1;
    return { state: { ...s, phase: 'play', discard, stockCount: stock.length, counts, takenFromDiscard: taken }, hands, stock };
  }

  if (s.phase !== 'play') throw new RuleError('Draw a card first (or drop)');

  if (type === 'spread') {
    const cards = (m as any).cards;
    if (!Array.isArray(cards) || cards.length < 3 || cards.length > 13 || !cards.every(isCard)) throw new RuleError('A spread is 3 or more cards');
    if (new Set(cards).size !== cards.length) throw new RuleError('Duplicate cards');
    if (!cards.every((c: Card) => hand.includes(c))) throw new RuleError('You do not hold those cards');
    const kind = spreadKind(cards);
    if (!kind) throw new RuleError('Not a spread: 3–4 of a kind, or 3+ in a row of one suit (aces low)');
    hands[seat] = hand.filter((c) => !cards.includes(c));
    counts[seat] = hands[seat].length;
    const spread: Spread = { id: s.nextSpreadId, owner: seat, kind, cards: kind === 'run' ? byRank(cards) : sortHand(cards) };
    const next: TonkState = { ...s, counts, spreads: [...s.spreads, spread], nextSpreadId: s.nextSpreadId + 1 };
    return { state: afterShed(next, hands, seat), hands, stock };
  }

  if (type === 'hit') {
    const card = (m as any).card;
    const id = Number((m as any).spread);
    if (!isCard(card) || !hand.includes(card)) throw new RuleError('You do not hold that card');
    const sp = s.spreads.find((x) => x.id === id);
    if (!sp) throw new RuleError('No such spread');
    if (!canHit(sp, card)) throw new RuleError(`${cardText(card)} doesn't fit that spread`);
    hands[seat] = hand.filter((c) => c !== card);
    counts[seat] = hands[seat].length;
    const cards = sp.kind === 'run' ? byRank([...sp.cards, card]) : sortHand([...sp.cards, card]);
    const next: TonkState = { ...s, counts, spreads: s.spreads.map((x) => (x.id === id ? { ...x, cards } : x)) };
    return { state: afterShed(next, hands, seat), hands, stock };
  }

  if (type === 'discard') {
    const card = (m as any).card;
    if (!isCard(card) || !hand.includes(card)) throw new RuleError('You do not hold that card');
    if (card === s.takenFromDiscard && hand.length > 1) throw new RuleError("You can't throw back the card you just took");
    hands[seat] = hand.filter((c) => c !== card);
    counts[seat] = hands[seat].length;
    let next: TonkState = { ...s, counts, discard: [...s.discard, card], takenFromDiscard: null };
    if (!hands[seat].length) return { state: endHand(next, hands, 'out', seat, null), hands, stock };
    const after = nextActive(s.active, seat);
    if (!stock.length) {
      // Stock out: lowest total wins, ties to whoever would play next.
      return { state: endHand(next, hands, 'stock', lowest(orderAfter(s.active, seat), hands), null), hands, stock };
    }
    next = { ...next, phase: 'draw', turn: after };
    return { state: next, hands, stock };
  }

  throw new RuleError('Unknown move');
}

// ---------------------------------------------------------------------------
// Computer players
// ---------------------------------------------------------------------------

/** How many cards in `hand` are "working" with `card` (pairs / near runs). */
function synergy(hand: Card[], card: Card): number {
  let n = 0;
  for (const c of hand) {
    if (c === card) continue;
    if (c[0] === card[0]) n += 1;
    else if (suitOf(c) === suitOf(card) && Math.abs(rankOf(c) - rankOf(card)) <= 2) n += Math.abs(rankOf(c) - rankOf(card)) === 1 ? 0.8 : 0.4;
  }
  return n;
}

/** The best spread to lay: the one that sheds the most points. */
function bestSpread(hand: Card[]): Card[] | null {
  const all = findSpreads(hand);
  if (!all.length) return null;
  return all.sort((a, b) => handTotal(b) - handTotal(a) || b.length - a.length)[0];
}

function bestHit(state: TonkState, hand: Card[]): { card: Card; spread: number } | null {
  let best: { card: Card; spread: number } | null = null;
  for (const c of hand) for (const sp of state.spreads) {
    if (canHit(sp, c) && (!best || cardValue(c) > cardValue(best.card))) best = { card: c, spread: sp.id };
  }
  return best;
}

export function botMove(state: TonkState, hand: Card[], seat: number): TonkMove {
  hand = Array.isArray(hand) ? hand : [];
  if (state.phase === 'draw') {
    const total = handTotal(hand);
    const opp = state.active.filter((s) => s !== seat);
    const minOpp = Math.min(...opp.map((s) => state.counts[s]));
    const limit = minOpp >= 5 ? 10 : minOpp >= 3 ? 6 : 3;
    if (total <= limit) return { type: 'drop' };
    const top = state.discard[state.discard.length - 1];
    if (top) {
      const withTop = [...hand, top];
      const makes = findSpreads(withTop).some((sp) => sp.includes(top));
      const hits = state.spreads.some((sp) => canHit(sp, top));
      const worst = Math.max(...hand.map(cardValue));
      if (makes || hits || (cardValue(top) <= 2 && worst >= 8) || state.stockCount === 0) return { type: 'draw', from: 'discard' };
    }
    return { type: 'draw', from: 'stock' };
  }
  // Play: spread, then hit, then discard the heaviest dead card.
  const sp = bestSpread(hand);
  if (sp) return { type: 'spread', cards: sp };
  const hit = bestHit(state, hand);
  if (hit) return { type: 'hit', ...hit };
  const choices = hand.length > 1 ? hand.filter((c) => c !== state.takenFromDiscard) : hand;
  const score = (c: Card) => cardValue(c) - synergy(hand, c) * 4;
  const card = choices.slice().sort((a, b) => score(b) - score(a) || rankOf(b) - rankOf(a))[0];
  return { type: 'discard', card };
}

// ---------------------------------------------------------------------------
// Table framework hookup (registered in registry.ts)
// ---------------------------------------------------------------------------

function privsOut(r: MoveOut, seats: number[]): Record<number, any> {
  const out: Record<number, any> = { [STOCK_ROW]: r.stock };
  for (const s of seats) out[s] = r.hands[s] ?? [];
  return out;
}

export const tonkGame: GameDef<TonkState, TonkMove> = {
  kind: 'tonk',
  label: 'Tonk',
  minPlayers: 2,
  maxPlayers: 4,
  seatNames: SEAT_NAMES,
  configure: (cur, patch) => {
    const p = (patch && typeof patch === 'object' ? patch : {}) as Record<string, unknown>;
    const hands = p.hands !== undefined ? Number(p.hands) : (cur?.hands ?? DEFAULT_HANDS);
    if (!HAND_OPTIONS.includes(hands)) throw new RuleError(`Hands must be one of ${HAND_OPTIONS.join(', ')}`);
    return { hands };
  },
  start: (prev, ctx) => {
    const { state, hands, stock } = deal(prev, ctx.rng, activeSeats(ctx.seats), clampHands(ctx.options?.hands));
    return { g: state, privs: { ...hands, [STOCK_ROW]: stock } };
  },
  turnSeat: (g) => (g.phase === 'draw' || g.phase === 'play' ? g.turn : null),
  move: (g, privs, seat, m) => {
    const r = applyMove(g, privs, seat, m);
    // A finished hand shows everyone's cards; while playing only the mover's hand changes.
    const ended = r.state.phase === 'hand_over' || r.state.phase === 'game_over';
    return { g: r.state, privs: privsOut(r, ended ? g.active : [seat]) };
  },
  bot: {
    move: (g, hand: Card[], seat) => botMove(g, hand, seat),
    names: ['Ace Bot', 'Deuce Bot', 'Trey Bot', 'Jack Bot'],
  },
  betweenHands: (g) => g.phase === 'hand_over',
  betweenHandsMs: 8_000,
  isOver: (g) => g.phase === 'game_over',
  results: (g) => g.active.map((seat): SeatResult => ({
    seat,
    outcome: g.winner === seat ? 'win' : g.drawn.includes(seat) ? 'draw' : 'loss',
    points: g.scores[seat],
  })),
  describe: (m, who) => {
    switch (m?.type) {
      case 'drop': return `${who} dropped!`;
      case 'draw': return m.from === 'discard' ? `${who} took the discard` : `${who} drew from the stock`;
      case 'spread': return `${who} spread ${m.cards.map(cardText).join(' ')}`;
      case 'hit': return `${who} hit with ${cardText(m.card)}`;
      case 'discard': return `${who} discarded ${cardText(m.card)}`;
      default: return `${who} moved`;
    }
  },
};
